// ============================================================================
// JARVISTRAVEL - UTILITY FUNCTIONS
// Version: 1.0.0
// ============================================================================

import { VALIDATION, FATIGUE_WEIGHTS, FATIGUE_LEVELS } from '@/app/constants';
import { toLocalYmd, fromLocalYmd, parseDateInput } from './dateOnly';
import type { 
  FatigueIndex, 
  FatigueLevel, 
  FatigueComponent,
  ItineraryDay,
  TripDates 
} from '@/app/types';

type FatigueBreakdown = Record<string, FatigueComponent>;
type DateRange = TripDates;

// -----------------------------------------------------------------------------
// VALIDATION UTILITIES
// -----------------------------------------------------------------------------

export const validateEmail = (email: string): boolean => {
  // Guard against absurdly long input (RFC 5321 caps an address at 254 chars).
  if (!email || email.length > 254) return false;
  return VALIDATION.email.test(email);
};

export const validateName = (name: string): boolean => {
  const trimmed = name.trim();
  return trimmed.length >= VALIDATION.name.minLength && 
         trimmed.length <= VALIDATION.name.maxLength;
};

// -----------------------------------------------------------------------------
// FORMATTING UTILITIES
// -----------------------------------------------------------------------------

/** An ISO 4217-shaped code: three letters, which is all Intl.NumberFormat will
 *  take. Shared so the save path and the display path cannot drift apart about
 *  what counts as a currency (#541 review). */
export const CURRENCY_CODE = /^[A-Za-z]{3}$/;

export const formatCurrency = (
  amount: number, 
  currency: string = 'USD', 
  locale: string = 'en-US'
): string => {
  // Whole-dollar display app-wide: clean and consistent (no lone "$1,292.5").
  // Validate currency code — an empty string or invalid code crashes
  // Intl.NumberFormat with RangeError. Closes JAR-288 PR #92 H1.
  const code = CURRENCY_CODE.test(currency) ? currency : 'USD';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/** The symbol formatCurrency prints before an amount ("$", "€", "CA$"), for a
 *  money field's prefix. */
export const currencySymbol = (currency: string, locale: string = 'en-US'): string => {
  const code = CURRENCY_CODE.test(currency) ? currency : 'USD';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: code })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? code;
};

/** Money to the cent, for the few places where rounding would destroy the point.
 *
 *  `formatCurrency` above rounds to whole units app-wide, which is right for a
 *  budget total and wrong wherever two amounts are shown to be COMPARED: a
 *  charge revised from 92.50 to 92.90 renders as "93" and "93" under it, so the
 *  row says the number changed and then prints the same number twice. Both
 *  figures always carry cents here, so they line up and the difference is
 *  legible even when it is small. */
export const formatCurrencyExact = (
  amount: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  const code = CURRENCY_CODE.test(currency) ? currency : 'USD';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatDate = (
  dateString: string, 
  options: Intl.DateTimeFormatOptions = { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  }
): string => {
  // A bare YYYY-MM-DD is a CALENDAR DATE, not an instant. `new Date('2026-07-28')`
  // parses as UTC midnight, which toLocaleDateString then renders in local time —
  // so every user west of Greenwich saw the previous day. "Jul 27" for a trip
  // starting the 28th, in New York and Los Angeles (JAR-610).
  //
  // Anything carrying a time or offset IS an instant (e.g. a subscription's
  // current_period_end) and must keep rendering in the viewer's local time.
  const date = parseDateInput(dateString);
  return date.toLocaleDateString('en-US', options);
};

// The guard and the dispatch moved to dateOnly, next to the parser they hand
// off to. They disagreed about what a calendar date is while living in separate
// files — the guard demanded zero-padding and the parser did not — so
// '2026-7-28' took the instant path and rendered correctly only by accident
// (JAR-610 L6, JAR-703).

export const formatDateRange = (dates: DateRange): string => {
  const checkin = formatDate(dates.checkin, { month: 'short', day: 'numeric' });
  const checkout = formatDate(dates.checkout, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${checkin} - ${checkout}`;
};

/**
 * Friendly relative time for feed-style items (notifications). Recent events read
 * "Just now" / "Nm ago" / "Nh ago" / "Yesterday"; older ones fall back to the
 * app's short date ("Jul 6") — never a raw locale string with seconds (JAR-365).
 * `now` is injectable for deterministic tests.
 */
export const formatRelativeTime = (dateString: string, now: number = Date.now()): string => {
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = now - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return formatDate(dateString, { month: 'short', day: 'numeric' });
};

/** A stored "HH:MM" as a 12-hour clock: "14:30" reads "2:30 PM". Anything that
 *  isn't one, such as an untimed stop's '', comes back as it is. */
export const formatTime = (timeString: string): string => {
  const match = /^(\d{1,2}):(\d{2})/.exec(timeString);
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return timeString;
  const hours = Number(match[1]);
  return `${hours % 12 || 12}:${match[2]} ${hours >= 12 ? 'PM' : 'AM'}`;
};

/**
 * A typed time as the "HH:MM" a stop stores, or null when the text isn't one.
 * Reads a 12-hour clock with or without the space or the M ("2:30 pm", "2:30pm",
 * "230p", "2pm") and a 24-hour one ("14:30", "1430"). A bare "2" is refused,
 * since it could be either.
 */
export const parseTime = (text: string): string | null => {
  const match = /^(\d{1,2})(?:[:.]?(\d{2}))?\s*(?:([ap])\.?\s*m?\.?)?$/i.exec(text.trim());
  if (!match) return null;
  const [, h, m, half] = match;
  let hours = Number(h);
  const minutes = Number(m ?? '0');
  if (minutes > 59) return null;
  if (half) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (half.toLowerCase() === 'p' ? 12 : 0);
  } else if (m === undefined || hours > 23) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const formatNumber = (num: number, locale: string = 'en-US'): string => {
  return new Intl.NumberFormat(locale).format(num);
};

export const formatPercentage = (value: number, decimals: number = 0): string => {
  return `${value.toFixed(decimals)}%`;
};

// -----------------------------------------------------------------------------
// DATE UTILITIES
// -----------------------------------------------------------------------------

export const getDaysBetween = (start: string, end: string): number => {
  const startDate = parseDateInput(start);
  const endDate = parseDateInput(end);
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  // Round, not ceil. Calendar dates are anchored at local noon, so a day that
  // crosses a DST transition is 23h or 25h rather than 24h — and ceil() turns
  // that 25h into 2 days. Rounding absorbs the ±1h shift; the noon anchor keeps
  // 12h of slack either side, so nothing else can push it over a boundary.
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

export const getDaysUntil = (dateString: string): number => {
  const targetDate = parseDateInput(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);
  const diffTime = targetDate.getTime() - today.getTime();
  // Round for the same DST reason as getDaysBetween above. Both endpoints are
  // normalised to local midnight here, so a transition between them makes the
  // span 23h or 25h.
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
};

export const isDateInRange = (date: string, range: DateRange): boolean => {
  const checkDate = parseDateInput(date);
  const start = parseDateInput(range.checkin);
  const end = parseDateInput(range.checkout);
  return checkDate >= start && checkDate <= end;
};

export const getDateArray = (start: string, end: string): string[] => {
  // Parsed and formatted on the same (local) side. The previous version parsed
  // as UTC midnight but stepped days with local getDate/setDate — self-cancelling
  // at a fixed offset, but not across a DST transition, where the shifted hour
  // moved the UTC-formatted date onto the wrong day (JAR-610).
  const dates: string[] = [];
  const currentDate = fromLocalYmd(start);
  const endDate = fromLocalYmd(end);

  while (currentDate <= endDate) {
    dates.push(toLocalYmd(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const isToday = (dateString: string): boolean => {
  const date = parseDateInput(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

export const isPast = (dateString: string): boolean => {
  const date = parseDateInput(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

// -----------------------------------------------------------------------------
// FATIGUE INDEX CALCULATIONS
// -----------------------------------------------------------------------------

export const calculateFatigueScore = (itinerary: ItineraryDay[]): number => {
  if (itinerary.length === 0) return 1;

  // Public FI is a 1–9 scale; a day's load tracks roughly with its activity
  // count (a 6-activity day ≈ Balanced, an 8+ day ≈ Packed).
  const total = itinerary.reduce(
    (sum, day) => sum + Math.min(9, Math.max(1, day.activities.length)),
    0,
  );
  return Math.max(1, Math.min(9, Math.round(total / itinerary.length)));
};

export const calculateFatigueBreakdown = (itinerary: ItineraryDay[]): FatigueBreakdown => {
  // Simplified calculation for demonstration
  const baseScore = calculateFatigueScore(itinerary);
  
  return {
    travelTime: {
      score: Math.min(baseScore * 0.8, 100),
      weight: FATIGUE_WEIGHTS.travelTime,
      description: 'Time spent in transit between locations',
    },
    activityDensity: {
      score: baseScore,
      weight: FATIGUE_WEIGHTS.activityDensity,
      description: 'Number of activities packed into each day',
    },
    restPeriods: {
      score: Math.max(100 - baseScore * 1.2, 0),
      weight: FATIGUE_WEIGHTS.restPeriods,
      description: 'Breaks and downtime between activities',
    },
    jetLag: {
      score: 20, // Would be calculated based on timezone changes
      weight: FATIGUE_WEIGHTS.jetLag,
      description: 'Impact of timezone changes',
    },
    walkingDistance: {
      score: Math.min(baseScore * 0.6, 100),
      weight: FATIGUE_WEIGHTS.walkingDistance,
      description: 'Estimated walking distance per day',
    },
  };
};

export const getFatigueLevel = (score: number): FatigueLevel => {
  if (score <= FATIGUE_LEVELS.Chill.max) return 'Chill';
  if (score <= FATIGUE_LEVELS.Balanced.max) return 'Balanced';
  return 'Packed';
};

export const getFatigueLevelColor = (level: FatigueLevel): string => {
  return FATIGUE_LEVELS[level].color;
};

export const generateFatigueRecommendations = (score: number, breakdown: FatigueBreakdown): string[] => {
  const recommendations: string[] = [];
  
  if (breakdown.activityDensity.score > 70) {
    recommendations.push('Consider removing 1-2 activities per day');
  }
  
  if (breakdown.restPeriods.score < 30) {
    recommendations.push('Schedule longer breaks between activities');
  }
  
  if (breakdown.travelTime.score > 60) {
    recommendations.push('Group nearby activities together');
  }
  
  if (breakdown.walkingDistance.score > 70) {
    recommendations.push('Consider using transportation for some segments');
  }
  
  if (score > 75) {
    recommendations.push('Your schedule is quite intensive - add buffer time');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('Your itinerary looks well-balanced!');
  }
  
  return recommendations;
};

export const buildFatigueIndex = (itinerary: ItineraryDay[]): FatigueIndex => {
  const score = calculateFatigueScore(itinerary);
  const breakdown = calculateFatigueBreakdown(itinerary);
  const level = getFatigueLevel(score);
  const recommendations = generateFatigueRecommendations(score, breakdown);
  
  return {
    score,
    level,
    breakdown,
    recommendations,
    lastUpdated: new Date().toISOString(),
  };
};

// -----------------------------------------------------------------------------
// STRING UTILITIES
// -----------------------------------------------------------------------------

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Upper-cases only the first character, leaving the rest untouched — safe for
// sentences that contain proper nouns (e.g. "moves St. Peter's…" → "Moves St. Peter's…").
export const capitalizeFirst = (str: string): string =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const truncate = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
};

export const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// -----------------------------------------------------------------------------
// ARRAY UTILITIES
// -----------------------------------------------------------------------------

export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((groups, item) => {
    const groupKey = String(item[key]);
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(item);
    return groups;
  }, {} as Record<string, T[]>);
};

export const sortBy = <T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export const unique = <T>(array: T[], key?: keyof T): T[] => {
  if (!key) {
    return [...new Set(array)];
  }
  
  const seen = new Set();
  return array.filter(item => {
    const value = item[key];
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

// -----------------------------------------------------------------------------
// STORAGE UTILITIES
// -----------------------------------------------------------------------------

export const getFromStorage = <T>(key: string, defaultValue: T): T => {
  if (typeof window === 'undefined') return defaultValue;
  
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const setToStorage = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const removeFromStorage = (key: string): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

// -----------------------------------------------------------------------------
// ASYNC HELPERS
// -----------------------------------------------------------------------------

export const asyncDelay = (ms: number = 0): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

// -----------------------------------------------------------------------------
// DEBOUNCE & THROTTLE
// -----------------------------------------------------------------------------

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

// -----------------------------------------------------------------------------
// SHARE UTILITIES
// -----------------------------------------------------------------------------


export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator === 'undefined') return false;
  
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
};
